import { HttpClient, HttpParams, HttpResponse } from "@angular/common/http";
import { EventEmitter, Injectable } from "@angular/core";
import { environment } from "projects/htl-part/src/environments/environment";
import { Observable, Subject, throwError } from "rxjs";
import { catchError, filter, map } from "rxjs/operators";
import { ShortItem } from "../shared/models/shortItem.model";

enum EndpointRoutes {
    SHORT_FREQUENCY_INSERT = "shortFrequency/insert",
    SHORT_FREQUENCY_UPDATE = "shortFrequency/update",
    SHORT_FREQUENCY_GET = "shortFrequency/get",
    SHORT_FREQUENCY_DELETE = "shortFrequency/delete",
    GET_INSPECTION_TYPES = "inspectionType/get",
    GET_HELICOPTER_DATA = "helicopter/getData",
    GET_HELICOPTER_WARNINGS = "helicopter/warnings/get",
}

@Injectable({
    providedIn: "root",
})
export class NextDueMaintenanceService {
    public shortItemsArray: ShortItem[] = [];
    public onShortItemsArrayChange: EventEmitter<void> = new EventEmitter<void>();
    public $currentHelicopterId: Subject<number> = new Subject<number>();

    constructor(private _http: HttpClient) {}

    private _updateArrayItem(shortTerm: ShortItem) {
        const filteredItem = this.shortItemsArray.filter((s) => s.id === shortTerm.id)[0];
        const i = this.shortItemsArray.indexOf(filteredItem);
        this.shortItemsArray[i] = shortTerm;
    }

    public getShortThermsByBlockId(blockId: number): Observable<ShortItem[]> {
        let params = new HttpParams();
        params = params.append("blockId", blockId.toString());
        return this._http.get<ShortItem[]>(environment.serverUrl + EndpointRoutes.SHORT_FREQUENCY_GET, { params }).pipe(
            map((responseData: ShortItem[]) => {
                this.shortItemsArray = responseData;
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(() => errorRes);
            }),
        );
    }

    public insertShortItem(shortItem: ShortItem): Observable<ShortItem> {
        return this._http.put<ShortItem>(environment.serverUrl + EndpointRoutes.SHORT_FREQUENCY_INSERT, { shortItem }).pipe(
            map((responseData) => {
                this.shortItemsArray.push(responseData);
                this.onShortItemsArrayChange.next();
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public updateShortItem(shortItem: ShortItem): Observable<ShortItem> {
        return this._http.post<ShortItem>(environment.serverUrl + EndpointRoutes.SHORT_FREQUENCY_UPDATE, { shortItem }).pipe(
            map((responseData) => {
                this._updateArrayItem(responseData);
                this.onShortItemsArrayChange.next();
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public getInspectionTypes(): Observable<{ id: number; name: string }[]> {
        return this._http.get<{ id: number; name: string }[]>(environment.serverUrl + EndpointRoutes.GET_INSPECTION_TYPES).pipe(
            map((responseData) => {
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public getAircraftData(hId: number): Observable<any> {
        let params = new HttpParams();
        params = params.append("helicopterId", hId.toString());
        return this._http.get<any>(environment.serverUrl + EndpointRoutes.GET_HELICOPTER_DATA, { params }).pipe(
            map((responseData) => {
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public deleteShortItem(shortItemId: number) {
        let params = new HttpParams();
        params = params.append("id", shortItemId.toString());
        return this._http.delete(environment.serverUrl + EndpointRoutes.SHORT_FREQUENCY_DELETE, { params }).pipe(
            map((responseData) => {
                this.shortItemsArray = this.shortItemsArray.filter((s) => s.id !== shortItemId);
                this.onShortItemsArrayChange.next();
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public getHelicopterWarnings(helicopterId: number) {
        let params = new HttpParams();
        params = params.append("helicopterId", helicopterId.toString());
        return this._http.get(environment.serverUrl + EndpointRoutes.GET_HELICOPTER_WARNINGS, { params }).pipe(
            map((responseData) => {
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public insertAccessory(shortItem: any) {
        return this._http.put<any>(environment.serverUrl + "shortFrequency/accessory/insert", { shortItem }).pipe(
            map((responseData) => {
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public updateAccessory(shortItem: any) {
        return this._http.post<any>(environment.serverUrl + "shortFrequency/accessory/update", { shortItem }).pipe(
            map((responseData) => {
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public getAccessories(helicopterId: number) {
        let params = new HttpParams();
        params = params.append("helicopterId", helicopterId.toString());
        return this._http.get<any[]>(environment.serverUrl + "shortFrequency/accessories/get", { params }).pipe(
            map((responseData) => {
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public deleteAccessory(shortItemId: number) {
        let params = new HttpParams();
        params = params.append("id", shortItemId.toString());
        return this._http.delete(environment.serverUrl + "shortFrequency/accessory/delete", { params }).pipe(
            map((responseData) => {
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public getHooks(helicopterId: number) {
        let params = new HttpParams();
        params = params.append("helicopterId", helicopterId.toString());
        return this._http.get<any[]>(environment.serverUrl + "helicopter/getHooks", { params }).pipe(
            map((responseData) => {
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public getHoists(helicopterId: number) {
        let params = new HttpParams();
        params = params.append("helicopterId", helicopterId.toString());
        return this._http.get<any[]>(environment.serverUrl + "helicopter/getHoists", { params }).pipe(
            map((responseData) => {
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }
}
